<template>
  <v-row>
    <v-col cols="12">
      <v-form ref="form" v-model="valid">
        <v-card>
          <v-card-title>
            {{ $t("offerForm.title", { year: booking.year }) }}
          </v-card-title>

          <v-card-text>
            <Markdown :value="$t('offerForm.description')" />
          </v-card-text>

          <v-simple-table class="table-no-hover">
            <template #default>
              <tbody>
                <tr v-for="(fund, index) in formData.funds" :key="index">
                  <td>
                    {{ fund.name }}
                  </td>

                  <td>
                    {{ $t(`bookings.fund.type.${fund.type}`) }}
                  </td>

                  <td v-for="(entry, providerIndex) in fund.prices" :key="entry.provider" width="15%">
                    <v-text-field
                      v-model.number="fund.prices[providerIndex].price"
                      :label="$t('offerForm.provider.price.label', { provider: providerShortName(entry.provider) })"
                      type="number"
                      min="0"
                      :rules="[amountRule]"
                      required
                    >
                      <template #append>€</template>
                    </v-text-field>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <v-simple-table class="table-no-hover">
            <template #default>
              <thead>
                <tr>
                  <th>{{ $t("offerForm.provider.title") }}</th>
                  <th>{{ $t("bookings.priceTable.netTotal") }}</th>
                  <th width="15%"></th>
                  <th>{{ $t("bookings.priceTable.taxTotal") }}</th>
                  <th>{{ $t("user.bookings.grossTotal.title") }}</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="(entry, providerIndex) in formData.taxes" :key="entry.provider">
                  <td>{{ providerName(entry.provider) }}</td>
                  <td>{{ formatCurrency(totals.blocks[providerIndex].netTotal) }}</td>
                  <td>
                    <v-text-field
                      v-model.number="formData.taxes[providerIndex].tax"
                      :label="$t('offerForm.provider.tax.label', { provider: providerShortName(entry.provider) })"
                      type="number"
                      min="0"
                      :rules="[amountRule]"
                      required
                    >
                      <template #append>%</template>
                    </v-text-field>
                  </td>
                  <td>+ {{ formatCurrency(totals.blocks[providerIndex].taxTotal) }}</td>
                  <td>{{ formatCurrency(totals.blocks[providerIndex].grossTotal) }}</td>
                </tr>
              </tbody>

              <tfoot>
                <tr>
                  <td>
                    <strong>{{ $t("bookings.priceTable.total.grossTotal") }}</strong>
                  </td>
                  <td>{{ formatCurrency(totals.netTotal) }}</td>
                  <td></td>
                  <td>+ {{ formatCurrency(totals.taxTotal) }}</td>
                  <td>
                    <strong>{{ formatCurrency(totals.grossTotal) }}</strong>
                  </td>
                </tr>
              </tfoot>
            </template>
          </v-simple-table>

          <v-card-actions>
            <v-dialog v-model="dialog" persistent max-width="600">
              <template #activator="{ on, attrs }">
                <v-btn :disabled="!valid" color="primary" class="mr-4" v-bind="attrs" v-on="on">
                  {{ $t("offerForm.offer") }}
                </v-btn>
              </template>

              <v-card>
                <v-card-title class="headline">
                  {{ $t("offerForm.confirmation.title") }}
                </v-card-title>

                <v-card-text>
                  <Markdown :value="$t('offerForm.confirmation.description')" />
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>

                  <v-btn color="warning" @click="dialog = false">
                    {{ $t("offerForm.confirmation.cancel") }}
                  </v-btn>

                  <v-btn color="primary" @click="offer" :loading="loading">
                    {{ $t("offerForm.confirmation.accept") }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-spacer></v-spacer>
            <v-dialog v-model="deleteDialog" persistent max-width="600">
              <template #activator="{ on, attrs }">
                <v-btn color="error" v-bind="attrs" v-on="on">
                  {{ $t("offerForm.delete") }}
                </v-btn>
              </template>

              <v-card>
                <v-card-title class="headline">
                  {{ $t("offerForm.deleteConfirmation.title") }}
                </v-card-title>

                <v-card-text>
                  {{ $t("offerForm.deleteConfirmation.description") }}
                </v-card-text>

                <v-card-actions>
                  <v-btn color="warning" @click="deleteDialog = false">
                    {{ $t("offerForm.deleteConfirmation.cancel") }}
                  </v-btn>

                  <v-spacer></v-spacer>

                  <v-btn color="error" @click="deleteBooking" :loading="deleting">
                    {{ $t("offerForm.deleteConfirmation.confirm") }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-col>
  </v-row>
</template>

<script>
import { map, pick } from "ramda";
import bookingProviders from "@fng/common/bookingProviders";
import calculateBookingTotals from "@fng/common/calculateBookingTotals";
import OfferBookingMutation from "@/gql/mutations/OfferBookingMutation.gql";
import DeleteBookingMutation from "@/gql/mutations/DeleteBookingMutation.gql";
import AdminUserQuery from "@/gql/queries/AdminUserQuery.gql";

const initFormData = (booking) => ({
  funds: booking.funds.map((fund) => ({
    id: fund.id,
    name: fund.name,
    type: fund.type,
    prices: bookingProviders.map(({ key }) => ({
      provider: key,
      price: (fund.prices || []).find((entry) => entry.provider === key)?.price ?? null,
    })),
  })),
  taxes: bookingProviders.map(({ key, defaultTax }) => ({
    provider: key,
    tax: (booking.taxes || []).find((entry) => entry.provider === key)?.tax ?? defaultTax,
  })),
});

export default {
  props: {
    booking: {
      type: Object,
      required: true,
    },
    userId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      formData: initFormData(this.booking),
      valid: false,
      loading: false,
      dialog: false,
      deleting: false,
      deleteDialog: false,
    };
  },

  computed: {
    totals() {
      return calculateBookingTotals(this.formData);
    },
  },

  methods: {
    amountRule(value) {
      return (value !== "" && value !== null && value >= 0) || this.$t("validation.required");
    },

    providerName(key) {
      return bookingProviders.find((provider) => provider.key === key)?.name;
    },

    providerShortName(key) {
      return bookingProviders.find((provider) => provider.key === key)?.shortName;
    },

    async deleteBooking() {
      this.deleting = true;

      await this.$apollo.mutate({
        mutation: DeleteBookingMutation,
        variables: { id: this.booking.id },
        refetchQueries: [
          {
            query: AdminUserQuery,
            variables: { id: this.userId },
          },
        ],
      });

      this.deleting = false;
      this.deleteDialog = false;
    },

    async offer() {
      this.loading = true;

      const input = {
        id: this.booking.id,
        funds: map(
          (fund) => ({
            id: fund.id,
            prices: map(pick(["provider", "price"]), fund.prices),
          }),
          this.formData.funds
        ),
        taxes: map(pick(["provider", "tax"]), this.formData.taxes),
      };

      await this.$apollo.mutate({
        mutation: OfferBookingMutation,
        variables: {
          input,
        },
      });

      this.dialog = false;
      this.loading = false;
    },

    formatCurrency: new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format,
  },
};
</script>
